import React, { useState, useRef, useCallback } from 'react';
import api from '../../../../services/api';
import './AnaliseRisco.css';

const AnaliseRisco = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setError(null);
        setAnalysisResult(null);
      } else {
        setError('Apenas arquivos PDF são permitidos');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setError(null);
        setAnalysisResult(null);
      } else {
        setError('Apenas arquivos PDF são permitidos');
      }
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setAnalysisResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/risk-analysis/standalone/analyze', formData);

      if (response.success) {
        setAnalysisResult(response.data);
      } else {
        setError(response.message || 'Erro ao analisar documento');
      }
    } catch (err) {
      console.error('Error analyzing document:', err);
      setError(err.response?.data?.message || 'Erro ao analisar documento. Tente novamente.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!analysisResult?.riskAnalysis) return;

    setIsGeneratingReport(true);

    try {
      const response = await api.post('/risk-analysis/standalone/report', {
        analysisData: analysisResult.riskAnalysis,
        fileName: analysisResult.fileName
      }, {
        responseType: 'blob'
      });

      // Create download link - response is already a blob from api.js
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_risco_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Erro ao gerar relatório. Tente novamente.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setAnalysisResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getRiskBandLabel = (band) => {
    const labels = {
      low: 'Baixo',
      medium: 'Médio',
      high: 'Alto'
    };
    return labels[band] || band;
  };

  const getScoreClass = (score) => {
    if (score >= 70) return 'high';
    if (score >= 30) return 'medium';
    return 'low';
  };

  return (
    <div className="analise-risco-container">
      <h1>Análise de Risco</h1>
      <p className="analise-risco-subtitle">
        Envie um Processo de Execução para análise de riscos jurídicos
      </p>

      {/* Upload Section */}
      <div className="upload-section">
        <div
          className={`upload-dropzone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleDropzoneClick}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="application/pdf"
            style={{ display: 'none' }}
          />

          {!selectedFile ? (
            <>
              <div className="upload-icon">📄</div>
              <p className="upload-text">
                Arraste um arquivo PDF aqui ou clique para selecionar
              </p>
              <p className="upload-hint">
                Tamanho máximo: 5MB
              </p>
            </>
          ) : (
            <div className="file-info">
              <span className="file-name">{selectedFile.name}</span>
              <span className="file-size">({formatFileSize(selectedFile.size)})</span>
              <button
                className="remove-file-btn"
                onClick={handleRemoveFile}
                title="Remover arquivo"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="action-buttons">
          <button
            className="analyze-btn"
            onClick={handleAnalyze}
            disabled={!selectedFile || isAnalyzing}
          >
            {isAnalyzing ? (
              <>
                <span className="loading-spinner" style={{ width: 20, height: 20, margin: 0 }}></span>
                Analisando...
              </>
            ) : (
              <>🔍 Analisar Documento</>
            )}
          </button>

          {(selectedFile || analysisResult) && (
            <button className="clear-btn" onClick={handleClear}>
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isAnalyzing && (
        <div className="upload-section">
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p className="loading-text">Analisando documento... Isso pode levar alguns segundos.</p>
          </div>
        </div>
      )}

      {/* Results Section */}
      {analysisResult && !isAnalyzing && (
        <div className="results-section">
          <div className="results-header">
            <h2>Resultado da Análise</h2>
            <button
              className="download-report-btn"
              onClick={handleDownloadReport}
              disabled={isGeneratingReport}
            >
              {isGeneratingReport ? (
                <>Gerando...</>
              ) : (
                <>📥 Baixar Relatório PDF</>
              )}
            </button>
          </div>

          {/* Risk Band Display */}
          <div className={`risk-band-display ${analysisResult.riskAnalysis.band}`}>
            <p className="risk-band-label">Nível de Risco</p>
            <p className="risk-band-value">
              {getRiskBandLabel(analysisResult.riskAnalysis.band)}
            </p>
            <p className="risk-score">
              Pontuação: {analysisResult.riskAnalysis.document_score}
            </p>
          </div>

          {/* Statistics */}
          <div className="stats-grid">
            <div className="stat-card">
              <p className="stat-value">{analysisResult.riskAnalysis.total_hits}</p>
              <p className="stat-label">Ocorrências</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">{analysisResult.riskAnalysis.total_pages}</p>
              <p className="stat-label">Páginas</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">{analysisResult.riskAnalysis.document_score}</p>
              <p className="stat-label">Pontuação</p>
            </div>
          </div>

          {/* Per-term counts */}
          {analysisResult.riskAnalysis.per_term_counts &&
           Object.keys(analysisResult.riskAnalysis.per_term_counts).length > 0 && (
            <div className="term-counts-section">
              <h3>Termos Encontrados</h3>
              <div className="term-counts-grid">
                {Object.entries(analysisResult.riskAnalysis.per_term_counts).map(([term, count]) => (
                  <div key={term} className="term-count-item">
                    <span className="term-name">{term}</span>
                    <span className="term-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hits table */}
          {analysisResult.riskAnalysis.hits && analysisResult.riskAnalysis.hits.length > 0 && (
            <div className="hits-section">
              <h3>Detalhes das Ocorrências</h3>
              <table className="hits-table">
                <thead>
                  <tr>
                    <th>Termo</th>
                    <th>Página</th>
                    <th>Trecho</th>
                    <th>Peso</th>
                  </tr>
                </thead>
                <tbody>
                  {analysisResult.riskAnalysis.hits.slice(0, 50).map((hit, index) => (
                    <tr key={index}>
                      <td className="hit-term">{hit.term}</td>
                      <td className="hit-page">{hit.page}</td>
                      <td className="hit-snippet" title={hit.snippet}>
                        {hit.snippet?.substring(0, 100)}...
                      </td>
                      <td className={`hit-score ${getScoreClass(hit.weight * 10)}`}>
                        {hit.weight}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {analysisResult.riskAnalysis.hits.length > 50 && (
                <p style={{ textAlign: 'center', marginTop: '1rem', color: '#6c757d' }}>
                  Mostrando 50 de {analysisResult.riskAnalysis.hits.length} ocorrências
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnaliseRisco;
